import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Text, Button, Image, Input, FormControl, FormLabel, FormErrorMessage, FormHelperText, useToast } from '@chakra-ui/react';
import { Lock, LocalShippingOutlined, ChevronRight, ArrowForward } from '@mui/icons-material';

import { getUserById, updateUser } from '../services/UserServices';
import { getPaymentConfig, payWithRazorpay } from '../services/PaymentServices';
import { detectCountry, parseStoredPhone, isValidPhoneFor, toStoredPhone } from '../validations/PhoneValidations';
import { useUserContext } from '../contexts/UserContext';
import { useCartContext } from '../contexts/CartContext';
import CartLine from '../components/CartLine';
import Breadcrumb from '../components/layout/Breadcrumb';
import useCatalog, { categoryImage } from '../hooks/useCatalog';
import { formatPrice } from '../components/product/productUtils';
import PhoneInput from '../components/PhoneInput';
import AddressForm, { emptyAddress, addressFromUser, composeAddress, validateAddress } from '../components/AddressForm';

// Brings the first highlighted field into view so the customer sees what to fix
const focusFirstError = () => setTimeout(() => {
  const field = document.querySelector('[aria-invalid="true"]');
  if (field) {
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    field.focus({ preventScroll: true });
  }
}, 50);

const Cart = () => {

  const toast = useToast();
  const navigate = useNavigate();
  const { currentUser } = useUserContext();
  const { items, itemCount: totalAmount, subtotal: totalPrice, setItemAmount, removeItem, clearCart } = useCartContext();
  const { categories, products } = useCatalog();

  const [user, setUser] = useState(null);
  const [address, setAddress] = useState(emptyAddress);
  const [phoneCountry, setPhoneCountry] = useState(detectCountry());
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [provider, setProvider] = useState('stripe');
  const [errors, setErrors] = useState({});
  const [isPaying, setIsPaying] = useState(false);


  useEffect(() => {
    currentUser && getUserById(currentUser)
      .then((result) => {
        setUser(result.user);
        setAddress(addressFromUser(result.user));
        if (result.user?.phone) {
          const parsed = parseStoredPhone(result.user.phone);
          setPhoneCountry(parsed.country);
          setPhone(parsed.number);
        }
        setEmail(result.user?.notifyEmail || result.user?.email || '');
      });
  }, [currentUser]);

  useEffect(() => {
    getPaymentConfig()
      .then(config => setProvider(config.provider))
      .catch(() => {});
  }, []);


  const validate = () => {
    const newErrors = validateAddress(address);
    if (!isValidPhoneFor(phoneCountry, phone)) {
      newErrors.phone = phoneCountry === 'IN' ? 'Enter a valid 10-digit mobile number.' : 'Enter a valid mobile number.';
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) newErrors.email = 'Enter the email where we should send your order updates.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onClickPay = async () => {
    if (!currentUser) {
      navigate('/login', { state: { from: '/cart' } });
      return;
    }
    if (!validate()) {
      focusFirstError();
      return;
    }

    const cleanAddress = composeAddress(address);
    const cleanPhone = toStoredPhone(phoneCountry, phone);
    const cleanEmail = email.trim().toLowerCase();
    setIsPaying(true);

    try {
      // Remember the address/phone for next time
      let payingUser = user;
      if (cleanAddress !== user?.address || cleanPhone !== user?.phone || cleanEmail !== (user?.notifyEmail || user?.email) || JSON.stringify(address) !== JSON.stringify(addressFromUser(user))) {
        const result = await updateUser(currentUser, cleanAddress, cleanPhone, address, cleanEmail);
        payingUser = result.user || { ...user, address: cleanAddress, phone: cleanPhone };
        setUser(payingUser);
      }

      if (provider !== 'razorpay') {
        navigate('/payment', { state: { price: totalPrice, address: cleanAddress } });
        return;
      }

      const order = await payWithRazorpay({ cart: items, user: { ...payingUser, email: cleanEmail }, userId: currentUser, address: cleanAddress, contactEmail: cleanEmail });
      if (!order) {
        setIsPaying(false);
        return;
      }
      clearCart();
      toast({
        title: 'Order placed!',
        description: 'Payment successful. You can track it in My Orders.',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
      navigate('/orders');
    } catch (error) {
      setIsPaying(false);
      toast({
        title: 'Payment not completed',
        description: error.response?.data?.error || error.message,
        status: 'error',
        duration: 4000,
        isClosable: true
      });
    }
  };

  const condensed = { fontWeight: 700, textTransform: 'uppercase', sx: { fontStretch: '75%' } };

  // Empty cart: same treatment as the cart drawer (design-system.md §4.6)
  if (items.length === 0) {
    return (
      <Box>
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Cart' }]} />
        <Box px='16px' py={{ base: '48px', md: '80px' }} textAlign='center' >
          <Text fontSize={{ base: '28px', md: '40px' }} {...condensed} >Your cart is empty</Text>
          <Text mt='8px' fontSize='15px' color='raano.muted' >Not sure where to start? Try one of these.</Text>
          <Box mt='28px' display='flex' flexDirection='column' gap='10px' maxWidth='380px' mx='auto' >
            {categories.slice(0, 3).map(c => (
              <Box key={c._id} as='button' display='flex' alignItems='center' gap='14px' p='10px' border='1px solid' borderColor='raano.border' borderRadius='8px' textAlign='left' _hover={{ borderColor: 'raano.black' }} onClick={() => navigate('/search', { state: { categoryId: c._id } })} >
                <Box width='56px' height='56px' bg='raano.imageBg' flexShrink={0} >{categoryImage(products, c._id) && <Image src={categoryImage(products, c._id)} alt='' width='100%' height='100%' objectFit='cover' />}</Box>
                <Text flex={1} fontSize='15px' fontWeight={600} >{c.name}</Text>
                <ChevronRight />
              </Box>
            ))}
          </Box>
          <Button mt='28px' variant='ink' height='48px' px='40px' fontSize='16px' rightIcon={<ArrowForward fontSize='small' />} onClick={() => navigate('/search', { state: {} })} >Shop all</Button>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Cart' }]} />
      <Box display='flex' flexDirection={{ base: 'column', md: 'row' }} gap={{ base: '24px', md: '40px' }} alignItems='flex-start' px={{ base: '12px', md: '40px' }} pt={{ base: '16px', md: '24px' }} pb={{ base: '32px', md: '56px' }} >

        {/* Lines */}
        <Box flex={1} width='100%' minWidth={0} >
          <Box display='flex' justifyContent='space-between' alignItems='baseline' >
            <Text as='h1' fontSize={{ base: '24px', md: '32px' }} {...condensed} >
              Your cart <Box as='span' fontSize='15px' fontWeight={400} textTransform='none' sx={{ fontStretch: '100%' }} color='raano.muted' >({totalAmount} {totalAmount === 1 ? 'item' : 'items'})</Box>
            </Text>
            <Text as='button' fontSize='14px' textDecoration='underline' onClick={clearCart} >Clear cart</Text>
          </Box>
          <Box display='flex' alignItems='center' gap='8px' mt='16px' px='12px' py='10px' bg='raano.surface' borderRadius='8px' fontSize='14px' >
            <LocalShippingOutlined sx={{ fontSize: 18 }} />
            <Text>Congratulations! Your order is eligible for <b>Free Delivery</b>.</Text>
          </Box>
          <Box height='4px' bg='raano.black' borderRadius='full' mt='8px' />
          {items.map(item => (
            <CartLine key={item.id} item={item} onChangeAmount={(amount) => setItemAmount(item.id, amount)} onRemove={() => removeItem(item.id)} />
          ))}
        </Box>

        {/* Checkout column */}
        <Box width={{ base: '100%', md: '420px' }} flexShrink={0} position={{ md: 'sticky' }} top={{ md: '138px' }} >
          <Box border='1px solid' borderColor='raano.border' borderRadius='8px' p={{ base: '16px', md: '20px' }} >
            {currentUser ? (
              <>
                <Text fontSize='18px' {...condensed} mb='14px' >Delivery details</Text>
                <AddressForm value={address} onChange={setAddress} errors={errors} onClearError={(field) => setErrors(prev => ({ ...prev, [field]: null }))} />
                <FormControl isInvalid={!!errors.email} mt='12px' >
                  <FormLabel fontSize='14px' mb='4px' >Email for order updates</FormLabel>
                  <Input type='email' inputMode='email' autoComplete='email' autoCapitalize='none' placeholder='you@example.com' borderRadius='4px' value={email} onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: null })); }} />
                  {errors.email ? <FormErrorMessage>{errors.email}</FormErrorMessage> : <FormHelperText fontSize='12px' >We'll send your confirmation and delivery updates here.</FormHelperText>}
                </FormControl>
                <FormControl isInvalid={!!errors.phone} mt='12px' >
                  <FormLabel fontSize='14px' mb='4px' >Mobile number</FormLabel>
                  <PhoneInput country={phoneCountry} number={phone} onChange={(country, number) => { setPhoneCountry(country); setPhone(number); setErrors(prev => ({ ...prev, phone: null })); }} />
                  <FormErrorMessage>{errors.phone}</FormErrorMessage>
                </FormControl>
                {Object.values(errors).some(Boolean) && <Text fontSize='13px' color='raano.error' mt='12px' >Please fix the highlighted details to continue.</Text>}
              </>
            ) : (
              <Box>
                <Text fontSize='18px' {...condensed} >Log in to check out</Text>
                <Text fontSize='14px' color='raano.muted' mt='4px' >We'll save your address for faster checkout next time.</Text>
              </Box>
            )}

            <Text mt='22px' fontSize='15px' fontWeight={600} >Order summary</Text>
            <Box fontSize='14px' mt='8px' >
              <Box display='flex' justifyContent='space-between' py='4px' ><Text color='raano.muted' >Order value ({totalAmount} {totalAmount === 1 ? 'item' : 'items'})</Text><Text>{formatPrice(totalPrice, { space: false })}</Text></Box>
              <Box display='flex' justifyContent='space-between' py='4px' ><Text color='raano.muted' >Shipping fee</Text><Text color='raano.success' fontWeight={600} >FREE</Text></Box>
              <Box display='flex' justifyContent='space-between' pt='10px' mt='6px' borderTop='1px solid' borderColor='raano.border' fontSize='16px' ><Text fontWeight={700} >GRAND TOTAL</Text><Text fontWeight={700} >{formatPrice(totalPrice, { space: false })}</Text></Box>
            </Box>

            <Button mt='18px' variant='ink' width='100%' height='48px' fontSize='17px' leftIcon={<Lock fontSize='small' />} isLoading={isPaying} loadingText='Opening payment…' onClick={onClickPay} >
              {currentUser ? `Pay ${formatPrice(totalPrice, { space: false })}` : 'Log in to pay'}
            </Button>
            <Text mt='10px' fontSize='12px' color='raano.muted' textAlign='center' >
              {provider === 'razorpay' ? 'UPI · Cards · Netbanking · Wallets, secured by Razorpay' : 'Cards, secured by Stripe'}
            </Text>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Cart;
